<template>
    <el-dialog
        class="device-print-dialog"
        :visible="visible"
        title="称重商品加工打印"
        width="500px"
        :show-close="false"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        @close="handleClose"
    >
        <!-- 商品信息展示 -->
        <div class="product-info">
            <div class="info-row">
                <span class="label">SKU ID:</span>
                <span class="value">{{ productDetail.skuId || '--' }}</span>
            </div>
            <div class="info-row">
                <span class="label">条形码:</span>
                <span class="value">{{ productDetail.skuCode || '--' }}</span>
            </div>
            <div class="info-row">
                <span class="label">商品名称:</span>
                <span class="value">{{ productDetail.skuName || '--' }}</span>
            </div>
            <div class="info-row">
                <span class="label">规格:</span>
                <span class="value">{{ productDetail.actualSpec || '--' }}</span>
            </div>
            <div
                v-if="productDetail.expirationType"
                class="info-row"
            >
                <span class="label">效期类型:</span>
                <span class="value">{{ expirationDesc }}</span>
            </div>
        </div>

        <!-- 表单区域 -->
        <el-form
            ref="devicePrintForm"
            :model="form"
            :rules="printFormRules"
            label-width="120px"
            label-position="left"
        >
            <!-- 生产设备 -->
            <el-form-item
                label="生产设备:"
                prop="deviceId"
                class="required-field"
            >
                <el-select
                    v-model="form.deviceId"
                    placeholder="请选择打印设备"
                    style="width: 200px;"
                    clearable
                    filterable
                >
                    <el-option
                        v-for="device in deviceList"
                        :key="device.deviceId"
                        :label="device.deviceName"
                        :value="device.deviceId"
                    />
                </el-select>
                <el-button
                    type="text"
                    class="manage-device-btn"
                    @click="handleManageDevice"
                >
                    管理设备
                </el-button>
            </el-form-item>

            <!-- 生产数量 -->
            <el-form-item
                label="生产数量:"
                prop="printQuantity"
                class="required-field"
            >
                <el-input
                    v-model="form.printQuantity"
                    placeholder="请输入数量"
                    style="width: 200px;"
                    type="number"
                />
            </el-form-item>

            <!-- 选择日期 -->
            <el-form-item
                label="选择日期:"
                prop="printDate"
                class="required-field"
            >
                <el-date-picker
                    v-model="form.printDate"
                    type="date"
                    placeholder="请选择日期"
                    style="width: 200px;"
                    value-format="timestamp"
                    :picker-options="pickerOptions"
                />
            </el-form-item>

            <!-- 选择时间 - 仅在效期类型为1、2、3、4时显示 -->
            <el-form-item
                v-if="shouldShowTimeSelect"
                prop="printTime"
                label="选择时间:"
                class="required-field"
            >
                <conditional-popover
                    :tipMessage="purchaseHalfDayTips"
                >
                    <el-select
                        v-model="form.printTime"
                        placeholder="请选择时间"
                        style="width: 200px;"
                        clearable
                        :disabled="isDisPrintTime"
                    >
                        <el-option
                            v-for="item in expiryTimeList"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        />
                    </el-select>
                </conditional-popover>
                <mc-announcement
                    v-if="alertMessageValue"
                    :title="alertMessageValue"
                    type="warning"
                    show-icon
                    style="margin-top: 10px;"
                />
                <mc-announcement
                    v-if="isHalfDay"
                    title="12点的标签请务必用黄色标签，0点请务必用白色标签，否则有拒收风险！"
                    type="warning"
                    show-icon
                    style="margin-top: 10px;"
                />
            </el-form-item>
            <template v-if="isABSelect && isABOrders">
                <el-form-item
                    prop="chainTagId"
                    label="请选择A/B单"
                >
                    <conditional-popover
                        :tipMessage="perHalfDayTips"
                    >
                        <el-select
                            v-model="form.chainTagId"
                            placeholder="请选择A/B单"
                            style="width: 100%;"
                            :disabled="isDisChainTagId"
                        >
                            <el-option
                                v-for="item in ABOrdersList"
                                :key="item.value"
                                :label="item.label"
                                :value="item.value"
                            />
                        </el-select>
                    </conditional-popover>
                    <mc-announcement
                        title="A单上市日期=所选日期+1；B单上市日期=所选日期+0；预采打印请准确判断AB单，否则会有拒收风险"
                        type="warning"
                        show-icon
                        style="margin-top: 10px;"
                    />
                </el-form-item>
            </template>
        </el-form>

        <!-- 底部按钮 -->
        <div
            slot="footer"
            class="dialog-footer"
        >
            <el-button
                type="primary"
                class="confirm-btn"
                :disabled="createDisabled"
                :loading="confirmLoading"
                @click="handleConfirm"
            >
                确认生产
            </el-button>
            <el-button
                class="cancel-btn"
                @click="handleClose"
            >
                取消
            </el-button>
        </div>
    </el-dialog>
</template>

<script>
import Rules from '@lsfe/rules';
import moment from 'moment';
import { EXPIRATION_TYPE_MAP, EXPIRATION_TYPE, PLAN_TYPE } from './constants';
import ConditionalPopover from '../ConditionalPopover';

export default {
    name: 'DevicePrintDialog',
    components: {
        ConditionalPopover,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        productInfo: {
            type: Object,
            default: () => ({}),
        },
        isABSelect: {
            type: Boolean,
            default: false,
        },
        //  计划类型 0：T0/今日预采  1：T1/明日预采
        planType: {
            type: [String, Number],
            default: '',
        },
        // 使用页面
        usePage: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isABOrders: false,
            ABOrdersList: [
                { label: 'A单：上市日期=所选日期+1', value: 1 },
                { label: 'B单：上市日期=所选日期+0', value: 2 },
            ],
            form: {
                deviceId: '', // 设备ID
                printQuantity: '',
                printDate: '',
                printTime: '', // 新增时间选择字段
                chainTagId: '',
            },
            confirmLoading: false,
            // 商品详细信息
            productDetail: {
                skuId: null,
                skuCode: '',
                skuName: '',
                actualSpec: '',
                expirationType: '',
                dateOptionList: [],
            },
            // 设备列表
            deviceList: [],
            printFormRules: {
                chainTagId: [
                    Rules.isRequired('请选择A/B单'),
                ],
                deviceId: [
                    Rules.isRequired('请选择生产设备'),
                ],
                printQuantity: [
                    Rules.isRequired('请输入生产数量'),
                    Rules.typeValid({ type: 'integer' }, '请填整数'),
                    {
                        validator: (rule, value, callback) => {
                            const max = 1000000;
                            if (value < 1 || value > max) {
                                callback(new Error(`范围1-${max}`));
                            } else {
                                callback();
                            }
                        },
                        trigger: 'change',
                    },
                ],
                printDate: [
                    Rules.isRequired('请选择日期'),
                ],
                printTime: [
                    Rules.isRequired('请选择时间'),
                ],
            },
            // 日期选择器选项
            pickerOptions: {
                disabledDate(time) {
                    // 只允许选择昨天、今天、明天
                    const today = new Date();
                    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
                    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

                    // 设置时间为当天的开始时间，避免时分秒的影响
                    yesterday.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);
                    tomorrow.setHours(0, 0, 0, 0);

                    const targetDate = new Date(time);
                    targetDate.setHours(0, 0, 0, 0);

                    return targetDate.getTime() < yesterday.getTime() || targetDate.getTime() > tomorrow.getTime();
                },
            },
            // 是否展示时间选择
            shouldShowTimeSelect: false,
            expiryTimeList: [],
            // 仅仅半天效期、小时批次、1/3批次、1/4批次会展示, 展示提示
            alertMessage: '',
            isDisChainTagId: false,
            isDisPrintTime: false,
            chainTagId: '', // 记录已经确定了A、B单的页面的单据类型
        };
    },
    computed: {
        expirationDesc() {
            return EXPIRATION_TYPE_MAP[this.productDetail.expirationType] || '未知';
        },
        createDisabled() {
            if (this.shouldShowTimeSelect) {
                return !(this.form.deviceId && +this.form.printQuantity > 0 && this.form.printDate && this.form.printTime);
            } else {
                return !(this.form.deviceId && +this.form.printQuantity > 0 && this.form.printDate);
            }
        },
        // 时间选择，附带的提示信息
        alertMessageValue() {
            return this.alertMessage;
        },
        // 半天批次
        isHalfDay() {
            const { expirationType } = this.productDetail;
            const { HALF_DAY } = EXPIRATION_TYPE;
            return expirationType === HALF_DAY;
        },
        // 预采单半天批次提示
        perHalfDayTips() {
            // preOrder 预采页面
            if (!(this.isHalfDay && this.usePage === 'preOrder')) return '';
            const { printTime } = this.form;
            if (printTime === '12:00') {
                return 'A单商品只能打印12点标签，不可选择0点';
            }
            if (printTime === '00:00') {
                return 'B单商品只能打0点标签，不可选择12点';
            }
            return '';
        },
        purchaseHalfDayTips() {
            // purchase 采购单页面
            if (!(this.isHalfDay && this.usePage === 'purchase')) return '';
            if (this.chainTagId === 1) {
                return '新规：A单采购单商品只能打印12点标签，不可选择0点';
            }
            if (this.chainTagId === 2) {
                return '新规：B单采购单商品只能打0点标签，不可选择12点';
            }
            return '';
        },
    },
    watch: {
        // 监听弹窗显示状态
        visible: {
            handler(newVisible) {
                if (newVisible) {
                    this.checkDeviceListAndInit();
                    // 弹窗显示时，如果有skuId则获取商品详细信息
                    if (this.productInfo.skuId) {
                        this.fetchProductDetail(this.productInfo.skuId);
                    }
                }
            },
            immediate: true,
        },
        'form.printTime': {
            handler(newV) {
                if (!this.isHalfDay || this.usePage !== 'preOrder') return;
                // 配置映射：时间 -> 对应的 chainTagId
                const timeToTagMap = {
                    '12:00': 1, // A单
                    '00:00': 2, // B单
                };
                const targetTagId = timeToTagMap[newV];
                // 只有匹配到配置时才执行赋值
                if (targetTagId !== undefined) {
                    this.form.chainTagId = targetTagId;
                    this.isDisChainTagId = true;
                }
            },
        },
    },
    mounted() {
        // 移除不存在的方法调用 - TODO by zhangzikuan
        // this.fetchDeviceList();
    },
    methods: {
        // 检查设备列表并初始化
        async checkDeviceListAndInit() {
            try {
                const response = await this.$hPost('/api/m/print/vss/query/device/list', {
                    supplierId: this.productInfo.supplierId,
                });
                const deviceList = response.autoDeviceList || [];

                if (deviceList.length === 0) {
                    this.$message.warning('暂无可用设备，请先添加设备');
                    // 关闭弹窗
                    this.$emit('close');
                    return;
                }

                // 设备列表不为空，更新设备列表
                this.deviceList = deviceList;
            } catch (error) {
                this.$message.error('获取设备列表失败');
                // 关闭弹窗
                this.$emit('close');
            }
        },
        // 获取商品详细信息
        async fetchProductDetail(skuId) {
            try {
                const res = await this.$hPost('/api/m/print/vss/query/print/task/skuInfo', {
                    skuId,
                    supplierId: this.productInfo.supplierId,
                    poiId: this.productInfo.poiId,
                    fromPreOrder: true,
                    billCode: this.productInfo.poNo,
                });
                this.productDetail = res || {};
                this.chainTagId = res.chainTagId;
                this.shouldShowTimeSelect = res?.dateOptionList && res?.dateOptionList?.length;
                this.isABOrders = res?.needChainTagId || false;

                if (this.shouldShowTimeSelect) {
                    // 半天效期、小时批次、1/3批次、1/4批次, 才会显示时间选择下拉框
                    const { dateOptionList = [] } = this.productDetail;
                    // 仅仅半天效期、小时批次、1/3批次、1/4批次，才会返回
                    // time "01:00"、"12:00"类似此类
                    this.expiryTimeList = dateOptionList.map((time) => ({
                        label: time,
                        value: time,
                    }));
                    this.alertMessage = res?.alertMessage;
                    // 初始化表单
                    this.initForm();
                }
            } catch (error) {
                this.alertMessage = '';
                this.expiryTimeList = [];
                this.$message.error('获取商品详细信息失败');
            }
        },


        // 管理设备
        handleManageDevice() {
            this.$tools.linkTo('/equipmentProcessing/list.html');
        },

        // 确认生产
        handleConfirm() {
            if (this.productDetail.skuId !== this.productInfo.skuId) {
                this.$message.error('当前商品信息和实际点击商品信息不匹配，请关闭弹窗重试');
                return;
            }
            this.$refs.devicePrintForm.validate(async (valid) => {
                if (valid) {
                    this.confirmLoading = true;
                    try {
                        // 构造请求参数
                        const params = {
                            deviceId: this.form.deviceId,
                            printQuantity: parseInt(this.form.printQuantity, 10),
                            purchaseCode: this.productInfo.poNo,
                            poiId: this.productInfo.poiId,
                            skuId: this.productInfo.skuId,
                            supplierId: this.productInfo.supplierId,
                        };

                        // 当codeType为2时，taskType传2，不传prePurchaseDate和prePurchaseType
                        if (this.productInfo.codeType === 2) {
                            params.taskType = 2;
                        }

                        if (this.productInfo.codeType === 3) {
                            params.taskType = 3;
                            params.prePurchaseDate = this.productInfo.prePurchaseDay;
                            params.prePurchaseType = this.productInfo.prePurchaseType;
                        }

                        // 处理任务时间
                        params.productDate = this.combineDateTime();
                        if (this.isABOrders) {
                            params.chainTagId = this.form.chainTagId;
                        }
                        // 调用设备打印接口
                        const response = await this.$hPost('/api/m/print/vss/create/print/task', params);

                        if (response) {
                            this.$message.success('设备打印任务已提交');
                            // 清空表单数据并关闭弹窗
                            this.resetForm();
                            this.handleClose();
                        }
                    // eslint-disable-next-line no-empty
                    } finally {
                        this.confirmLoading = false;
                    }
                } else {
                    this.$message.error('请完善表单信息');
                }
            });
        },

        // 合并日期和时间为时间戳
        combineDateTime() {
            const { printDate, printTime } = this.form;

            // 确保 printDate 存在
            if (!printDate) return ''; // 或返回默认值

            if (this.shouldShowTimeSelect) {
                // 需要时间选择
                if (printDate && printTime !== '') {
                    // 合并日期和时间
                    const datePart = moment(printDate).format('YYYY-MM-DD');
                    return moment(`${datePart} ${printTime}`).format('YYYY-MM-DD HH:mm');
                } else {
                    // 只有日期没有时间
                    return moment(printDate).format('YYYY-MM-DD 00:00');
                }
            } else {
                // 不需要时间选择，取当日开始时间
                return moment(printDate).startOf('day').format('YYYY-MM-DD HH:mm');
            }
        },

        // 重置表单
        resetForm() {
            if (this.$refs.devicePrintForm) {
                this.$refs.devicePrintForm.resetFields();
            }
            this.form = {
                deviceId: '',
                printQuantity: '',
                printDate: '',
                printTime: '',
            };
        },

        // 关闭弹窗
        handleClose() {
            // 重置表单
            this.resetForm();
            this.confirmLoading = false;
            this.$emit('close');
        },
        initForm() {
            if (!this.isHalfDay) return;

            // 1. 处理计划类型逻辑
            const planTypeMap = {
                [PLAN_TYPE.T1]: '12:00',
                [PLAN_TYPE.T0]: '00:00',
            };
            if (planTypeMap[this.planType] && this.usePage === 'preOrder') {
                this.form.printTime = planTypeMap[this.planType];
                this.isDisChainTagId = true;
            }
            const chainTagMap = {
                1: '12:00',
                2: '00:00',
            };
            if (chainTagMap[this.chainTagId] && this.usePage === 'purchase') {
                this.form.printTime = chainTagMap[this.chainTagId];
                this.isDisPrintTime = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.device-print-dialog {
    /deep/ .el-dialog {
        border-radius: 8px;
    }


    /deep/ .el-dialog__body {
        padding: 16px 20px;
    }

    /deep/ .el-form-item {
        margin-bottom: 18px;
    }

    .product-info {
        background: #f8f9fa;
        padding: 16px;
        border-radius: 6px;
        margin-bottom: 16px;

        .info-row {
            display: flex;
            margin-bottom: 8px;
            font-size: 14px;

            &:last-child {
                margin-bottom: 0;
            }

            .label {
                color: #666;
                width: 80px;
                flex-shrink: 0;
            }

            .value {
                color: #333;
                font-weight: 500;
            }
        }
    }

    .required-field {
        /deep/ .el-form-item__label {
            &::before {
                content: '*';
                color: #f56c6c;
                margin-right: 4px;
            }
        }
    }

    .manage-device-btn {
        margin-left: 12px;
        color: #409eff;
        font-size: 14px;
    }

    .dialog-footer {
        text-align: center;
        padding: 10px 0 16px;

        .el-button {
            min-width: 80px;
            margin: 0 8px;
        }

        .preview-btn {
            background: #5dade2;
            border-color: #5dade2;

            &:hover {
                background: #3498db;
                border-color: #3498db;
            }
        }

        .confirm-btn {
            background: #85c1e9;
            border-color: #85c1e9;

            &:hover {
                background: #5dade2;
                border-color: #5dade2;
            }
        }

        .cancel-btn {
            background: #fff;
            border-color: #dcdfe6;
            color: #606266;

            &:hover {
                background: #f5f7fa;
                border-color: #c6e2ff;
                color: #409eff;
            }
        }
    }
}
</style>
